import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Observable, forkJoin, of } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';

import { AllocationService } from './allocation.service';
import { CompanyService } from './company.service';
import { AllocationModel } from 'app/models/allocation.model';
import {
    ActualAllocationEditorResult,
} from 'app/modules/shifts/list/actual-allocation-editor-dialog/actual-allocation-editor-dialog.component';
import {
    CloseCurrentAllocationsDialogComponent,
    CloseCurrentAllocationsResult,
    OpenConflictItem,
} from 'app/modules/shifts/list/close-current-allocations-dialog/close-current-allocations-dialog.component';
import {
    AllocationMultiCompanyDialogComponent,
} from 'app/modules/shifts/list/allocation-multi-company-dialog/allocation-multi-company-dialog.component';
import {
    AllocationMultiCompanyAction,
    AllocationMultiCompanyDialogResult,
    SHIFT_ALLOCATION_MULTI_COMPANY,
} from 'app/modules/shifts/list/allocation-multi-company-dialog/allocation-multi-company.types';
import { PermissionService } from 'app/core/services/permission.service';

export interface NewAllocationRequest {
    vehicleId: number | null;
    shiftId: number | null;
    startedAt: string;
    users: { user_id: number; role: string }[];
}

/**
 * Orquestra as ações de alocação real (criar, fechar, atualizar, deletar,
 * remover pessoa) com suporte a replicar a mesma ação em várias empresas —
 * compartilhado entre /shifts/allocations e o widget de equipe remota, que
 * são cross-company por natureza.
 */
@Injectable({ providedIn: 'root' })
export class AllocationActionsService {
    accessibleCompanies: { company_id: number; name: string }[] = [];

    constructor(
        private readonly dialog: MatDialog,
        private readonly allocationService: AllocationService,
        private readonly companyService: CompanyService,
        private readonly permissionService: PermissionService,
        private readonly toastr: ToastrService,
    ) {}

    get canMultiCompanyAllocation(): boolean {
        return this.permissionService.hasPermission(SHIFT_ALLOCATION_MULTI_COMPANY) && this.accessibleCompanies.length > 1;
    }

    loadAccessibleCompanies(): void {
        this.companyService.listAppTenantCompanies().subscribe({
            next: (items) => {
                this.accessibleCompanies = (items || [])
                    .filter((c) => c?.company_id)
                    .map((c) => ({ company_id: c.company_id, name: c.name?.trim() || `Empresa #${c.company_id}` }));
            },
            error: () => {
                this.accessibleCompanies = [];
            },
        });
    }

    /** Pergunta em quais empresas replicar a ação; null = cancelado, [companyId] = só a atual quando multi-company não se aplica. */
    promptMultiCompanyScope(currentCompanyId: number, action: AllocationMultiCompanyAction): Observable<number[] | null> {
        if (!this.canMultiCompanyAllocation) {
            return of([currentCompanyId]);
        }
        return this.openMultiCompanyDialog(action, currentCompanyId);
    }

    /**
     * Igual a `promptMultiCompanyScope`, mas sem empresa de partida (nenhuma
     * pré-marcada) — usado ao criar um plantão cross-company do zero (ex.:
     * widget de equipe remota), onde não existe uma "empresa atual".
     */
    promptCompanyScopeNoDefault(action: AllocationMultiCompanyAction): Observable<number[] | null> {
        return this.openMultiCompanyDialog(action, null);
    }

    private openMultiCompanyDialog(
        action: AllocationMultiCompanyAction,
        currentCompanyId: number | null,
    ): Observable<number[] | null> {
        const ref = this.dialog.open(AllocationMultiCompanyDialogComponent, {
            width: 'min(94vw, 28rem)',
            data: {
                action,
                currentCompanyId,
                currentCompanyName:
                    (currentCompanyId != null && this.companyService.getCurrentCompany()?.name?.trim()) ||
                    (currentCompanyId != null ? `Empresa #${currentCompanyId}` : ''),
                companies: this.accessibleCompanies,
            },
        });
        return new Observable((subscriber) => {
            ref.afterClosed().subscribe((result: AllocationMultiCompanyDialogResult | undefined) => {
                if (!result || result.mode === 'cancel') subscriber.next(null);
                else subscriber.next(result.companyIds);
                subscriber.complete();
            });
        });
    }

    /**
     * Verifica (em lote) se alguém dos usuários informados já tem turno aberto
     * na empresa e, se sim, abre o dialog de resolução antes de continuar.
     * `onDone` é chamado depois de resolvido (ou direto, se não houver conflito).
     */
    checkOpenAllocationThenContinue(
        timezone: string,
        users: { user_id: number; role: string }[],
        vehicleId: number | null,
        userNameById: Map<number, string>,
        onDone: () => void,
    ): void {
        if (!users.length) {
            onDone();
            return;
        }
        forkJoin(users.map((u) => this.allocationService.getOpenByUser(u.user_id))).subscribe({
            next: (openAllocationsByUser) => {
                const conflicts: OpenConflictItem[] = [];
                users.forEach((current, i) => {
                    const openAllocations = openAllocationsByUser[i] || [];
                    const relevant = openAllocations.filter((a) => {
                        if (vehicleId != null && current.role === 'DRIVER' && a.vehicle_id != null && a.vehicle_id !== vehicleId) {
                            return true;
                        }
                        return a.vehicle_id !== vehicleId;
                    });
                    for (const a of relevant) {
                        conflicts.push({
                            allocation_id: a.allocation_id,
                            userId: current.user_id,
                            userName: userNameById.get(current.user_id) || `Usuário #${current.user_id}`,
                            label: a.vehicle?.name || (a.vehicle_id != null ? `V${a.vehicle_id}` : 'Sem viatura'),
                            started_at: a.started_at!,
                            crew: (a.allocation_users || [])
                                .filter((au) => au.user_id !== current.user_id)
                                .map((au) => ({ user_id: au.user_id, name: au.user?.name || `Usuário #${au.user_id}` })),
                        });
                    }
                });
                if (!conflicts.length) {
                    onDone();
                    return;
                }
                this.resolveOpenConflicts(timezone, conflicts, onDone);
            },
            error: () => onDone(),
        });
    }

    private resolveOpenConflicts(timezone: string, conflicts: OpenConflictItem[], onDone: () => void): void {
        const ref = this.dialog.open(CloseCurrentAllocationsDialogComponent, {
            width: 'min(94vw, 30rem)',
            data: { timezone, conflicts },
        });
        ref.afterClosed().subscribe((res: CloseCurrentAllocationsResult | undefined) => {
            if (!res || res.mode === 'cancel') return;
            if (!res.closures.length) {
                onDone();
                return;
            }
            const closureJobs = res.closures.map((closure) => {
                const conflict = conflicts.find((c) => c.allocation_id === closure.allocation_id);
                const totalCrew = 1 + (conflict?.crew.length ?? 0);
                if (closure.userIds.length >= totalCrew) {
                    return this.allocationService.closeById(closure.allocation_id, closure.ended_at);
                }
                return forkJoin(closure.userIds.map((userId) => this.allocationService.removeUser(closure.allocation_id, userId)));
            });
            forkJoin(closureJobs).subscribe({
                next: () => onDone(),
                error: () => onDone(),
            });
        });
    }

    /** Cria a alocação na empresa atual e, se escolhido, replica nas demais empresas. */
    createAcrossCompanies(
        currentCompanyId: number,
        request: NewAllocationRequest,
        companyIds: number[],
        onDone: () => void,
    ): void {
        const jobs = companyIds.map((companyId) =>
            companyId === currentCompanyId
                ? this.upsertPrimaryCompanyAllocation(request, companyId)
                : this.upsertSecondaryCompanyAllocation(request, companyId),
        );
        forkJoin(jobs.map((j) => j.pipe(catchError(() => of(null))))).subscribe({
            next: onDone,
            error: onDone,
        });
    }

    /**
     * Cria a alocação em cada empresa informada diretamente (sem checar
     * elegibilidade prevista) — usado por quem não depende de escala por
     * empresa, como o médico regulador.
     */
    createDirectAcrossCompanies(request: NewAllocationRequest, companyIds: number[], onDone: () => void): void {
        const jobs = companyIds.map((companyId) => this.upsertPrimaryCompanyAllocation(request, companyId));
        forkJoin(jobs.map((j) => j.pipe(catchError(() => of(null))))).subscribe({
            next: onDone,
            error: onDone,
        });
    }

    private upsertPrimaryCompanyAllocation(request: NewAllocationRequest, companyId: number): Observable<AllocationModel | null> {
        return this.allocationService
            .upsert({
                company_id: companyId,
                vehicle_id: request.vehicleId,
                shift_id: request.shiftId,
                append_to_open: true,
                started_at: request.startedAt,
                users: request.users.map((u) => ({ user_id: u.user_id, role: u.role as any, shift_id: request.shiftId })),
            })
            .pipe(
                catchError((err) => {
                    const msg = err?.error?.descricao || err?.error?.message;
                    if (msg) this.toastr.error(msg);
                    throw err;
                }),
            );
    }

    private upsertSecondaryCompanyAllocation(request: NewAllocationRequest, companyId: number): Observable<AllocationModel | null> {
        return this.allocationService.getEligibleUsers(companyId, request.startedAt).pipe(
            switchMap((eligible) => {
                const byId = new Map((eligible?.users || []).map((u) => [Number(u.user_id), u]));
                const users = request.users
                    .filter((u) => byId.has(u.user_id))
                    .map((u) => ({
                        user_id: u.user_id,
                        role: u.role as any,
                        shift_id: byId.get(u.user_id)?.planned_shift_id ?? null,
                    }));
                if (!users.length) return of(null);
                const shiftId = users.find((u) => u.shift_id != null)?.shift_id ?? null;
                return this.allocationService.upsert({
                    company_id: companyId,
                    vehicle_id: null,
                    shift_id: shiftId,
                    append_to_open: true,
                    started_at: request.startedAt,
                    users,
                });
            }),
            catchError(() => of(null)),
        );
    }

    closeAcrossCompanies(
        currentCompanyId: number,
        allocationId: number,
        endedAt: string,
        userIds: number[],
        companyIds: number[],
        onDone: () => void,
    ): void {
        const jobs = companyIds.map((companyId) =>
            companyId === currentCompanyId
                ? this.allocationService.closeById(allocationId, endedAt)
                : this.applyToOtherCompany(companyId, userIds, 'all', undefined, (a) =>
                      this.allocationService.closeById(a.allocation_id, endedAt),
                  ),
        );
        this.runJobs(jobs, onDone);
    }

    removePersonAcrossCompanies(
        currentCompanyId: number,
        allocationId: number,
        userId: number,
        companyIds: number[],
        onDone: () => void,
    ): void {
        const jobs = companyIds.map((companyId) =>
            companyId === currentCompanyId
                ? this.allocationService.removeUser(allocationId, userId)
                : this.applyToOtherCompany(companyId, [userId], 'person', userId, (a) =>
                      this.allocationService.removeUser(a.allocation_id, userId),
                  ),
        );
        this.runJobs(jobs, onDone);
    }

    updateScheduleAcrossCompanies(
        currentCompanyId: number,
        allocationId: number,
        startedAt: string,
        endedAt: string | undefined,
        userIds: number[],
        companyIds: number[],
        onDone: () => void,
    ): void {
        const jobs = companyIds.map((companyId) =>
            companyId === currentCompanyId
                ? this.allocationService.updateSchedule(allocationId, startedAt, endedAt)
                : this.applyToOtherCompany(companyId, userIds, 'all', undefined, (a) =>
                      this.allocationService.updateSchedule(a.allocation_id, startedAt, endedAt),
                  ),
        );
        this.runJobs(jobs, onDone);
    }

    deleteAcrossCompanies(
        currentCompanyId: number,
        allocationId: number,
        userIds: number[],
        companyIds: number[],
        onDone: () => void,
    ): void {
        const jobs = companyIds.map((companyId) =>
            companyId === currentCompanyId
                ? this.allocationService.deleteById(allocationId)
                : this.applyToOtherCompany(companyId, userIds, 'all', undefined, (a) =>
                      this.allocationService.deleteById(a.allocation_id),
                  ),
        );
        this.runJobs(jobs, onDone);
    }

    private applyToOtherCompany(
        companyId: number,
        userIds: number[],
        mode: 'all' | 'person',
        personUserId: number | undefined,
        action: (allocation: AllocationModel) => Observable<unknown>,
    ): Observable<unknown> {
        return this.allocationService.getOpenByCompany(companyId).pipe(
            switchMap((open) => {
                const targets = this.matchOpenAllocations(open || [], userIds, mode, personUserId);
                return targets.length ? forkJoin(targets.map((a) => action(a))) : of([]);
            }),
        );
    }

    private matchOpenAllocations(
        allocations: AllocationModel[],
        userIds: number[],
        mode: 'all' | 'person',
        personUserId?: number,
    ): AllocationModel[] {
        return (allocations || []).filter((a) => {
            if (a.ended_at) return false;
            const members = (a.allocation_users || []).map((u) => Number(u.user_id));
            if (mode === 'person' && personUserId != null) {
                return members.includes(Number(personUserId));
            }
            return userIds.some((id) => members.includes(Number(id)));
        });
    }

    private runJobs(jobs: Observable<unknown>[], onDone: () => void): void {
        forkJoin(jobs.map((j) => j.pipe(catchError((err) => {
            const msg = err?.error?.descricao || err?.error?.message;
            if (msg) this.toastr.error(msg);
            return of(null);
        })))).subscribe({
            next: onDone,
            error: onDone,
        });
    }
}
